import { useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, ImagePlus, Loader2, Trash2, UtensilsCrossed } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { AuthImage } from '@/components/auth-image';
import { ImageLightbox } from '@/components/image-lightbox';
import { form_error_message } from '@/lib/forms';
import { MAX_MENU_IMAGES } from '@/features/menu-image/api';
import {
  use_delete_menu_image,
  use_menu_images,
  use_reorder_menu_images,
  use_upload_menu_image,
} from '@/features/menu-image/hooks';

const ACCEPT = 'image/jpeg,image/png,image/webp';

/**
 * Cafe/restaurant menu (client change, 9 Oct 2026): no typed categories or items — the
 * vendor uploads photos of the menu card and customers page through them in this order.
 */
export function VendorMenuImagesPage() {
  const images_q = use_menu_images();
  const upload_m = use_upload_menu_image();
  const reorder_m = use_reorder_menu_images();
  const delete_m = use_delete_menu_image();
  const input_ref = useRef<HTMLInputElement>(null);
  const [error, set_error] = useState<string | null>(null);
  const [open_at, set_open_at] = useState<number | null>(null);

  const images = images_q.data ?? [];
  const room = MAX_MENU_IMAGES - images.length;
  const busy = upload_m.isPending || reorder_m.isPending || delete_m.isPending;

  async function handle_files(e: React.ChangeEvent<HTMLInputElement>) {
    set_error(null);
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length > room) {
      set_error(`You can add ${room} more page${room === 1 ? '' : 's'} (max ${MAX_MENU_IMAGES}).`);
      return;
    }
    // One at a time so pages keep the order they were picked in.
    for (const file of files) {
      try {
        await upload_m.mutateAsync(file);
      } catch (err) {
        set_error(form_error_message(err, `Could not upload ${file.name}.`));
        return;
      }
    }
  }

  async function move(index: number, by: -1 | 1) {
    const ids = images.map((i) => i.id);
    const target = index + by;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    set_error(null);
    try {
      await reorder_m.mutateAsync(ids);
    } catch (err) {
      set_error(form_error_message(err, 'Could not reorder the menu.'));
    }
  }

  async function remove(id: string) {
    if (!window.confirm('Remove this menu page?')) return;
    set_error(null);
    try {
      await delete_m.mutateAsync(id);
    } catch (err) {
      set_error(form_error_message(err, 'Could not remove the page.'));
    }
  }

  const add_button = (
    <Button
      variant="gradient"
      size="sm"
      onClick={() => input_ref.current?.click()}
      disabled={busy || room <= 0}
      data-testid="menu-image-add"
    >
      {upload_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
      Upload menu pages
    </Button>
  );

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader
        title="Menu"
        description={`Upload photos of your menu card — customers swipe through them in this order. Up to ${MAX_MENU_IMAGES} pages.`}
        action={add_button}
      />
      <input
        ref={input_ref}
        type="file"
        accept={ACCEPT}
        multiple
        className="hidden"
        onChange={handle_files}
        data-testid="menu-image-input"
      />

      {error && (
        <p className="text-sm text-destructive" data-testid="menu-image-error">
          {error}
        </p>
      )}

      {images_q.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading menu…
        </div>
      ) : images_q.isError ? (
        <ErrorState description={form_error_message(images_q.error, 'Could not load your menu.')} />
      ) : images.length === 0 ? (
        <EmptyState
          icon={UtensilsCrossed}
          title="No menu yet"
          description="Take clear photos of each page of your menu and upload them. Customers see them on your page."
          action={add_button}
        />
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="menu-image-grid">
          {images.map((img, i) => (
            <Card key={img.id} data-testid={`menu-image-${img.id}`}>
              <CardContent className="flex flex-col gap-2 p-3">
                <button
                  type="button"
                  onClick={() => set_open_at(i)}
                  className="aspect-[3/4] overflow-hidden rounded-md border-2 border-input bg-muted"
                  aria-label={`View page ${i + 1}`}
                >
                  <AuthImage path={`${img.file_path}?variant=medium`} alt={`Menu page ${i + 1}`} className="h-full w-full object-cover" />
                </button>
                <div className="flex items-center justify-between gap-1">
                  <span className="text-xs font-semibold text-muted-foreground">Page {i + 1}</span>
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => move(i, -1)} disabled={busy || i === 0} aria-label="Move earlier" data-testid={`menu-image-left-${img.id}`}>
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => move(i, 1)} disabled={busy || i === images.length - 1} aria-label="Move later" data-testid={`menu-image-right-${img.id}`}>
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => remove(img.id)} disabled={busy} aria-label="Remove page" data-testid={`menu-image-delete-${img.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {open_at !== null && (
        <ImageLightbox
          paths={images.map((i) => i.file_path)}
          index={open_at}
          on_close={() => set_open_at(null)}
          on_navigate={set_open_at}
        />
      )}
    </div>
  );
}
